import { Request, Response, NextFunction } from 'express';
import { AllocationRequestService } from '../services/allocationRequest.service';
import { Container } from 'typedi';
import { Types } from 'mongoose';
import { pick, removenull } from '../../../utils/helpers/utilities.services';
import { HttpException } from '../../../utils/exceptions/httpException';
import { jsonStatus, status } from '../../../utils/helpers/api.responses';
import { AllocationRequestStatusEnum } from '../interfaces/allocationRequest.interface';


export class AllocationRequestController {
    public allocationRequestService = Container.get(AllocationRequestService);

    public createAllocationRequest = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const allocationData = pick(req.body, [
                'userIds',
                'message',
                'startDate',
                'endDate',
                // 'location',
                'wage',
                'jobId',
                // 'companyName',
                'allocationNotes'
            ]);
            removenull(allocationData);
            allocationData.userIds = Array.isArray(allocationData.userIds)
                ? allocationData.userIds.map(id => new Types.ObjectId(id))
                : [new Types.ObjectId(allocationData.userIds)];
            if (!allocationData.userIds.length) {
                throw new HttpException(status.BadRequest, req.t('General.noFieldsProvided'));
            }
            const createdBy = new Types.ObjectId(req.user._id);
            const t = req.t;
            const allocationRequestData = {
                ...allocationData,
                createdBy,
            };
            const allocationRequests = await this.allocationRequestService.createAllocationRequest(
                allocationRequestData,
                t
            );
            res.status(status.Create).json({
                status: jsonStatus.Create,
                message: req.t('General.add_success', { field: req.t('AllocationRequest.allocationRequest') }),
                data: allocationRequests,
            });
        } catch (error) {
            next(error);
        }
    }

    public getAllocationRequestById = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { id } = req.params;
            const t = req.t;
            const allocationRequest = await this.allocationRequestService.getAllocationRequestById(id, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('AllocationRequest.allocationRequest') }),
                data: allocationRequest,
            });
        } catch (error) {
            next(error);
        }
    }
    public getPendingAllocationRequestsForUser = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { page = 1, limit = 10, sort = {} } = req.query;
            const { userId } = req.params;
            const t = req.t;

            if (!Types.ObjectId.isValid(userId)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'userId' }));
            }

            const filters: any = {
                userId: new Types.ObjectId(userId),
                status: AllocationRequestStatusEnum.PENDING,
                isDeleted: false,
            };

            const allocationRequests = await this.allocationRequestService.getAllAllocationRequests(
                filters,
                parseInt(page as string),
                parseInt(limit as string),
                sort,
                t
            );

            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_all_success', { field: t('AllocationRequest.allocationRequestPending') }),
                data: allocationRequests.data,
                total: allocationRequests.total,
                page: allocationRequests.page,
                totalPage: allocationRequests.totalPage,
                count: allocationRequests.statusCounts,
            });
        } catch (error) {
            next(error);
        }
    };



    public getAllAllocationRequests = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { page = 1, limit = 10, sort = {}, userId, allocationStatus, startDate, endDate, createdBy, location } = req.query;
            const filters = req.query.filters ? JSON.parse(req.query.filters as string) : {};
            if (userId) {
                filters.userId = Array.isArray(userId)
                    ? userId.map(id => id)
                    : [new Types.ObjectId(userId as string)];
            }
            if (allocationStatus) {
                filters.status = allocationStatus;
            }
            if (startDate) filters.startDate = { $gte: new Date(startDate as string) };
            if (endDate) filters.endDate = { $lte: new Date(endDate as string) };
            if (location) filters.location = new RegExp(location as string, 'i');
            if (createdBy) filters.createdBy = new Types.ObjectId(createdBy as string);
            const t = req.t;
            const allocationRequests = await this.allocationRequestService.getAllAllocationRequests(
                filters,
                parseInt(page as string),
                parseInt(limit as string),
                sort,
                t
            );
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('AllocationRequest.allocationRequest') }),
                data: allocationRequests.data,
                total: allocationRequests.total,
                page: allocationRequests.page,
                totalPage: allocationRequests.totalPage,
                count: allocationRequests.statusCounts,
            });
        } catch (error) {
            next(error);
        }
    }

    public respondToAllocationRequest = async (req: Request, res: Response, next: NextFunction) => {

        try {
            const { id } = req.params;
            const { allocationStatus, responseNotes } = req.body;
            const updatedBy = req.user._id;
            const t = req.t;

            const allocationRequest = await this.allocationRequestService.respondToAllocationRequest(
                id,
                allocationStatus.toLowerCase(),
                responseNotes,
                updatedBy,
                t
            );
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('AllocationRequest.sentAllocationResponse'),
                data: allocationRequest,
            });
        } catch (error) {
            next(error);
        }
    }

    public getAllocationHistory = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { page = 1, limit = 10, sort = {}, userId } = req.query;
            const filters = req.query.filters ? JSON.parse(req.query.filters as string) : {};
            if (userId) {
                filters.userId = Array.isArray(userId)
                    ? userId.map(id => id)
                    : [new Types.ObjectId(userId as string)];
            }
            const t = req.t;
            const history = await this.allocationRequestService.getAllocationHistory(
                filters,
                parseInt(page as string),
                parseInt(limit as string),
                sort,
                t
            );
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('AllocationRequest.history') }),
                data: history.data,
                total: history.total,
                page: history.page,
                totalPage: history.totalPage,
            });
        } catch (error) {
            next(error);
        }
    }

    public updateAllocationRequest = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { id } = req.params;
            const userData = pick(req.body, [
                'userId',
                'message',
                'startDate',
                'endDate',
                // 'location',
                'wage',
                'allocationNotes'
                // 'companyName',
            ]);
            removenull(userData);
            if (userData.userId) {
                userData.userId = new Types.ObjectId(userData.userId);
            }
            const updatedBy = new Types.ObjectId(req.user._id);
            const t = req.t;

            if (Object.keys(userData).length === 0) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'at least one update field' }));
            }

            const updateData = {
                ...userData,
                updatedBy
            };

            const allocationRequest = await this.allocationRequestService.updateAllocationRequest(id, updateData, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.update_success', { field: t('AllocationRequest.allocationRequest') }),
                data: allocationRequest,
            });
        } catch (error) {
            next(error);
        }
    }

    public deleteAllocationRequest = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { ids } = req.body;
            const updatedBy = req.user._id;
            const t = req.t;

            if (!Array.isArray(ids) || ids.length === 0) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'ids[]' }));
            }
            const result = await this.allocationRequestService.deleteMultipleAllocationRequests(ids, updatedBy, t);
            res.status(status.OK).json({
                success: jsonStatus.OK,
                message: t('General.delete_success', { field: t('AllocationRequest.allocationRequest') }),
                data: result,
            });
        } catch (error) {
            next(error);
        }
    }

    public getAllottedLabourers = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const jobId = req.params.jobId;
            const page = parseInt(req.query.page as string) || 1;
            const limit = parseInt(req.query.limit as string) || 10;
            const sort = req.query.sort ? JSON.parse(req.query.sort as string) : { 'userId.firstName': 1 };
            const t = req.t;

            const result = await this.allocationRequestService.getAllottedLabourers(jobId, page, limit, sort, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_all_success', { field: t('AllocationRequest.AllocatedUser') }),
                ...result
            });
        } catch (error) {
            next(error);
        }
    };

    public getRequestedLabourers = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const jobId = req.params.jobId;
            const page = parseInt(req.query.page as string) || 1;
            const limit = parseInt(req.query.limit as string) || 10;
            const sort = req.query.sort ? JSON.parse(req.query.sort as string) : { 'userId.firstName': 1 };
            const t = req.t;

            const result = await this.allocationRequestService.getRequestedLabourers(jobId, page, limit, sort, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_all_success', { field: t('AllocationRequest.requestedUser') }),
                ...result
            });
        } catch (error) {
            next(error);
        }
    };

}